/* ============================================================
   Physician's Choice: 2026 redesign runtime
   Replaces the three engines PC Header owned in the design export:
     1. scroll reveal   data-reveal="0|80|160" (ms stagger)
     2. count-up        <span data-count="60" data-suffix="B">60B</span>
     3. accordion       .pc-acc-item / .pc-acc-q / .pc-acc-a  (§7b)

   Scoped to .pc2026 roots only. Safe to load on every page: it does
   nothing if no .pc2026 root is present, and it is idempotent, so
   Shopify's section re-render events can re-run it freely.
   ============================================================ */

(function () {
  'use strict';

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- 1. Scroll reveal ---------------------------- */

  function initReveal(root) {
    var items = root.querySelectorAll('[data-reveal]:not([data-reveal-bound])');
    if (!items.length) return;

    // Without IntersectionObserver, or under reduced motion, show
    // everything immediately and never add the hiding attribute.
    if (REDUCED || !('IntersectionObserver' in window)) {
      for (var i = 0; i < items.length; i++) {
        items[i].setAttribute('data-reveal-bound', '');
        items[i].classList.add('pc-in');
      }
      return;
    }

    // Only now is it safe for CSS to hide un-revealed elements.
    root.setAttribute('data-reveal-ready', '');

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var delay = parseInt(el.getAttribute('data-reveal'), 10) || 0;
        setTimeout(function () { el.classList.add('pc-in'); }, delay);
        io.unobserve(el);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });

    for (var j = 0; j < items.length; j++) {
      items[j].setAttribute('data-reveal-bound', '');
      io.observe(items[j]);
    }
  }

  /* ---------- 2. Count-up --------------------------------- */

  function easeOut(t) { return 1 - Math.pow(1 - t, 3); }

  function runCount(el) {
    var target = parseFloat(el.getAttribute('data-count'));
    if (isNaN(target)) return;
    var suffix = el.getAttribute('data-suffix') || '';
    var prefix = el.getAttribute('data-prefix') || '';
    // Decimal places are inferred from the target so "4.6" counts to 4.6.
    var str = String(target);
    var dot = str.indexOf('.');
    var places = dot === -1 ? 0 : str.length - dot - 1;
    var duration = 1400;
    var start = null;

    // Years must not get thousands separators; counts must keep them.
    var grouped = el.getAttribute('data-group') !== 'false' && target >= 10000;

    function format(v) {
      if (!grouped) return v.toFixed(places);
      return Number(v.toFixed(places)).toLocaleString('en-US', {
        minimumFractionDigits: places, maximumFractionDigits: places
      });
    }

    function frame(ts) {
      if (start === null) start = ts;
      var p = Math.min((ts - start) / duration, 1);
      var value = target * easeOut(p);
      el.textContent = prefix + format(value) + suffix;
      if (p < 1) requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);
  }

  function initCount(root) {
    var items = root.querySelectorAll('[data-count]:not([data-count-bound])');
    if (!items.length) return;

    if (REDUCED || !('IntersectionObserver' in window)) {
      for (var i = 0; i < items.length; i++) items[i].setAttribute('data-count-bound', '');
      return; // leave the server-rendered final value in place
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        runCount(entry.target);
        io.unobserve(entry.target);
      });
    }, { threshold: 0.4 });

    for (var j = 0; j < items.length; j++) {
      items[j].setAttribute('data-count-bound', '');
      io.observe(items[j]);
    }
  }

  /* ---------- 3. Accordion (§7b) -------------------------- */

  function closeItem(item) {
    var panel = item.querySelector('.pc-acc-a');
    var btn = item.querySelector('.pc-acc-q');
    item.setAttribute('data-open', 'false');
    if (btn) btn.setAttribute('aria-expanded', 'false');
    if (!panel) return;
    // Set the measured height first so the transition has somewhere to go.
    panel.style.maxHeight = panel.scrollHeight + 'px';
    requestAnimationFrame(function () { panel.style.maxHeight = '0px'; });
  }

  function openItem(item) {
    var panel = item.querySelector('.pc-acc-a');
    var btn = item.querySelector('.pc-acc-q');
    item.setAttribute('data-open', 'true');
    if (btn) btn.setAttribute('aria-expanded', 'true');
    if (!panel) return;
    panel.style.maxHeight = panel.scrollHeight + 'px';
  }

  function initAccordion(root) {
    var items = root.querySelectorAll('.pc-acc-item:not([data-acc-bound])');
    for (var i = 0; i < items.length; i++) {
      (function (item) {
        item.setAttribute('data-acc-bound', '');
        var btn = item.querySelector('.pc-acc-q');
        if (!btn) return;
        if (item.getAttribute('data-open') !== 'true') item.setAttribute('data-open', 'false');
        btn.setAttribute('aria-expanded', item.getAttribute('data-open'));
        if (item.getAttribute('data-open') === 'true') openItem(item);

        btn.addEventListener('click', function () {
          var open = item.getAttribute('data-open') === 'true';
          if (open) { closeItem(item); return; }
          // Single-open per column group, matching the export.
          var group = item.closest('.pc-acc') || root;
          var siblings = group.querySelectorAll('.pc-acc-item[data-open="true"]');
          for (var s = 0; s < siblings.length; s++) closeItem(siblings[s]);
          openItem(item);
        });
      })(items[i]);
    }
  }

  // An open panel pinned to a stale pixel height breaks on resize.
  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      var open = document.querySelectorAll('.pc2026 .pc-acc-item[data-open="true"] .pc-acc-a');
      for (var i = 0; i < open.length; i++) open[i].style.maxHeight = open[i].scrollHeight + 'px';
    }, 150);
  });

  /* ---------- 4. Ambient clips ---------------------------- */
  /* Browsers routinely refuse or defer autoplay for a video that is far
     below the fold. The result looks like a frozen GIF rather than a
     paused video, so nudge each clip to play once it is actually on
     screen, and again if the tab regains focus. */

  function initVideos(root) {
    var vids = root.querySelectorAll('video[autoplay]:not([data-pc-vid-bound])');
    if (!vids.length) return;

    function play(v) {
      var p = v.play();
      if (p && p.catch) p.catch(function () { /* autoplay refused, leave the frame up */ });
    }

    if (!('IntersectionObserver' in window)) {
      for (var i = 0; i < vids.length; i++) { vids[i].setAttribute('data-pc-vid-bound', ''); play(vids[i]); }
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) play(entry.target);
        else if (!entry.target.paused) entry.target.pause();
      });
    }, { threshold: 0.15 });

    for (var j = 0; j < vids.length; j++) {
      vids[j].setAttribute('data-pc-vid-bound', '');
      io.observe(vids[j]);
    }
  }

  document.addEventListener('visibilitychange', function () {
    if (document.hidden) return;
    var vids = document.querySelectorAll('.pc2026 video[autoplay][data-pc-vid-bound]');
    for (var i = 0; i < vids.length; i++) {
      var v = vids[i], r = v.getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > 0 && v.paused) {
        var p = v.play(); if (p && p.catch) p.catch(function () {});
      }
    }
  });

  /* ---------- 5. Nav height ------------------------------- */
  /* The hero is pulled up under the fixed header by exactly the space the
     theme reserves above the first section. That height changes with the
     announcement bar and the breakpoint, so measure it rather than guess. */

  function syncNavHeight() {
    var header = document.querySelector('.pc-header') ||
                 document.querySelector('[id*="shopify-section"][id*="header"] > *');
    if (!header) return;
    var h = Math.round(header.getBoundingClientRect().height);
    if (h > 40 && h < 320) {
      var roots = document.querySelectorAll('.pc2026');
      for (var i = 0; i < roots.length; i++) roots[i].style.setProperty('--pc-nav-h', h + 'px');
    }
  }

  var navTimer;
  window.addEventListener('resize', function () {
    clearTimeout(navTimer);
    navTimer = setTimeout(syncNavHeight, 120);
  });

  /* ---------- 6. Arrow-driven track ----------------------- */
  /* Native horizontal scrolling on the partner row was capturing wheel and
     trackpad gestures, which made the page feel like it locked. The row no
     longer scrolls itself; the arrows step it one card at a time. */

  function initTracks(root) {
    var tracks = root.querySelectorAll('[data-pc-track]:not([data-pc-track-bound])');
    for (var i = 0; i < tracks.length; i++) {
      (function (track) {
        track.setAttribute('data-pc-track-bound', '');
        var scope = track.closest('section') || root;
        var prev = scope.querySelector('[data-pc-track-prev]');
        var next = scope.querySelector('[data-pc-track-next]');

        function step() {
          var card = track.firstElementChild;
          if (!card) return track.clientWidth;
          var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 20;
          return card.getBoundingClientRect().width + gap;
        }
        function maxLeft() { return Math.max(0, track.scrollWidth - track.clientWidth); }
        function sync() {
          var l = track.scrollLeft, m = maxLeft();
          if (prev) { prev.disabled = l <= 1; prev.style.opacity = l <= 1 ? '0.35' : '1'; }
          if (next) { next.disabled = l >= m - 1; next.style.opacity = l >= m - 1 ? '0.35' : '1'; }
        }
        function go(dir) {
          track.scrollLeft = Math.max(0, Math.min(maxLeft(), track.scrollLeft + dir * step()));
          setTimeout(sync, 320);
        }
        if (prev) prev.addEventListener('click', function () { go(-1); });
        if (next) next.addEventListener('click', function () { go(1); });
        track.addEventListener('scroll', sync);
        sync();
        window.addEventListener('resize', sync);
      })(tracks[i]);
    }
  }

  /* ---------- 7. Top-rated sort --------------------------- */
  /* Shopify has no "top rated" sort. Every collection on this store fits on
     one page, so the rendered grid is the whole result set and reordering it
     here is the real answer, not a partial one. */

  function initSort(root) {
    var sel = root.querySelector('[data-pc-sort]:not([data-pc-sort-bound])');
    if (!sel) return;
    sel.setAttribute('data-pc-sort-bound', '');

    sel.addEventListener('change', function (e) {
      if (sel.value !== 'pc-top-rated') return;
      e.stopPropagation();
      e.preventDefault();

      var grid = root.querySelector('.pc-grid--4');
      if (!grid) return;
      var cards = Array.prototype.slice.call(grid.querySelectorAll('.pc-pcard'));
      cards.sort(function (a, b) {
        return (parseFloat(b.getAttribute('data-rating')) || 0) -
               (parseFloat(a.getAttribute('data-rating')) || 0);
      });
      cards.forEach(function (c) { grid.appendChild(c); });
    }, true);
  }

  /* ---------- boot ---------------------------------------- */

  function init() {
    var roots = document.querySelectorAll('.pc2026');
    for (var i = 0; i < roots.length; i++) {
      initReveal(roots[i]);
      initCount(roots[i]);
      initAccordion(roots[i]);
      initVideos(roots[i]);
      initTracks(roots[i]);
      initSort(roots[i]);
    }
    syncNavHeight();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: re-bind when a section is re-rendered or loaded.
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:select', init);

  window.PC2026 = { init: init };
})();
